// Whether one currency P&L category may be removed from
// app_settings.pnl_expense_categories, and what to say when it may not.
//
// Run: deno test --allow-read src/features/pnlAnalytics/categoryRemoval.test.ts
// Run: npm run verify:pnl-category-removal
//
// WHY THIS GUARD EXISTS. Removing a category only edits a JSONB array in
// app_settings. Every row already typed under that name in
// claims_pnl_monthly_values survives untouched, and from that moment nothing
// on any screen names them: the project P&L tab renders one row per
// CONFIGURED category, so an orphan is simply absent, while the company
// dashboard (useDashboardPnl) sums every manual row it finds and so kept
// subtracting the orphan from profit. That is the "Ordinary Hours" /
// "Overtime Hours" defect of 8 September 2026, where two categories were
// created, valued, and then removed, leaving two rows on Aire - Newstead
// that no screen could show and one screen still counted.
//
// An orphan still deducts once it exists: that is plan decision D4, encoded
// as expense.unmapped.is_deductible = true and as the `if (!cat)` branch in
// pnlCalculationCore.ts, and both engines are tested against it. So this
// guard is the defence, not a friendlier message in front of one: it stops
// the orphan being created at all, which is also the only version where the
// numbers a user typed are still reachable. There is no database constraint
// to lean on here either, because the categories are a JSONB array rather
// than rows with a foreign key, which is what separates this from
// canDeleteOperationalMetric.
//
// WHY ADMIN ONLY. claims_pnl_monthly_values' SELECT policy shows a caller
// only the rows for projects they are a member of, created, or administer for
// the whole organisation. A non-admin's count of the values under a category
// can therefore come back zero while values exist on projects they cannot
// see, and this guard would cheerfully allow the removal that orphans them.
// Only an admin's count is complete, which is the same reason
// canDeleteOperationalMetric is admin only. It is also what the database
// already enforces from the other side: app_settings' UPDATE policy requires
// is_org_admin, so a non-admin's category save matches zero rows and returns
// no error, the silent-revert failure this codebase has been bitten by
// before.
//
// No React and no supabase here: the caller does the counting and the
// writing, this module only decides.

export interface OccupiedProjectMonth {
  /** claims_projects.job_location, as shown everywhere else in the app. */
  projectName: string;
  /** claims_pnl_monthly_values.month_date, "YYYY-MM-01". */
  monthDate: string;
  /** The typed amount. Zero counts: a zero someone typed is still their data. */
  amount: number;
}

export interface CategoryRemovalInput {
  categoryName: string;
  /** Every project-month in this organisation still holding a value under that exact category string. */
  occupied: OccupiedProjectMonth[];
  /** Whether the viewer is a super_admin or admin of this organisation. See the admin note above. */
  viewerIsOrgAdmin: boolean;
  /**
   * Set when the count could not be taken (a failed query). Treated as "cannot
   * prove it is empty", which must refuse: falling through to an allow here
   * would orphan values precisely when the system is least sure of itself.
   */
  countFailed?: boolean;
}

export type CategoryRemovalRefusal = "not_an_admin" | "count_failed" | "has_values";

export type CategoryRemovalVerdict =
  | { allowed: true }
  | {
      allowed: false;
      refusal: CategoryRemovalRefusal;
      /** Dialog heading. */
      title: string;
      /** Plain sentences, safe to show a user as is. */
      message: string;
    };

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * "2026-08-01" to "Aug 2026", by string surgery rather than Date.
 *
 * A month_date is a calendar label, not an instant. Parsing it into a Date
 * makes it an instant at UTC midnight, which in Australia/Brisbane is still
 * the previous month for the first of any month, so "2026-08-01" would print
 * as Jul 2026 for every user of this app. An unrecognisable string is
 * returned unchanged rather than guessed at.
 */
export function formatMonthLabel(monthDate: string): string {
  const parts = monthDate.split("-");
  if (parts.length < 2) return monthDate;
  const year = parts[0];
  const monthIndex = Number(parts[1]) - 1;
  if (!/^\d{4}$/.test(year) || !Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return monthDate;
  }
  return `${MONTH_NAMES[monthIndex]} ${year}`;
}

/** "1 value" / "7 values", so a refusal never reads "1 values". */
export function describeValueCount(count: number): string {
  return count === 1 ? "1 value" : `${count} values`;
}

/**
 * Money as "$1,234.50", built by hand rather than through Intl.
 *
 * Intl.NumberFormat's grouping and currency symbol follow the runtime's
 * default locale unless one is pinned, so the same refusal message could
 * read differently in a test, in Deno and in a browser. This is one line of
 * output in one dialog; determinism is worth more here than locale support.
 */
export function formatAmount(amount: number): string {
  const negative = amount < 0;
  const fixed = Math.abs(amount).toFixed(2);
  const [whole, cents] = fixed.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}$${grouped}.${cents}`;
}

/** How many project-months the message names before it stops listing and counts the rest. */
export const MAX_LISTED_PROJECT_MONTHS = 6;

/**
 * Renders the occupied project-months as "Aire - Newstead (Aug 2026, Sep
 * 2026) and Hendra (Jul 2026)", grouped by project in the order the rows
 * arrive, so the user reads one place per line of work rather than the same
 * project name six times.
 */
export function describeOccupiedProjectMonths(occupied: OccupiedProjectMonth[]): string {
  const byProject = new Map<string, string[]>();
  let listed = 0;
  let omitted = 0;

  for (const row of occupied) {
    if (listed >= MAX_LISTED_PROJECT_MONTHS) {
      omitted++;
      continue;
    }
    const months = byProject.get(row.projectName) ?? [];
    months.push(formatMonthLabel(row.monthDate));
    byProject.set(row.projectName, months);
    listed++;
  }

  const phrases = [...byProject.entries()].map(([project, months]) => `${project} (${months.join(", ")})`);
  if (omitted > 0) {
    phrases.push(`${omitted} more`);
  }

  if (phrases.length === 0) return "";
  if (phrases.length === 1) return phrases[0];
  return `${phrases.slice(0, -1).join(", ")} and ${phrases[phrases.length - 1]}`;
}

/**
 * Decides whether this currency category may be removed from the editor's
 * list. Removal is refused while any project-month still holds a value under
 * it, and the refusal names the count, the money and the months so the user
 * can go and clear them.
 */
export function canRemovePnlCategory(input: CategoryRemovalInput): CategoryRemovalVerdict {
  // Broadest reason first, so the message names why this user cannot do it
  // rather than the last check to fail. `has_values` is last on purpose:
  // being told which months to clear is only useful advice once the removal
  // is one you could otherwise make.
  if (!input.viewerIsOrgAdmin) {
    return {
      allowed: false,
      refusal: "not_an_admin",
      title: "Only an admin can change the category list",
      message:
        "P&L categories are shared by every project in the organisation, and only an admin can see every project's values, " +
        "so only an admin can safely remove one. Ask an organisation admin to remove it for you.",
    };
  }

  if (input.countFailed) {
    return {
      allowed: false,
      refusal: "count_failed",
      title: "Could not check for typed values",
      message:
        `Nothing has been removed. The check for values still typed under "${input.categoryName}" did not complete, ` +
        "so removing it now could leave numbers behind with no row to show them. Try again in a moment.",
    };
  }

  if (input.occupied.length === 0) {
    return { allowed: true };
  }

  const total = input.occupied.reduce((sum, row) => sum + row.amount, 0);
  const where = describeOccupiedProjectMonths(input.occupied);
  // Zero is the case the Aire rows are actually in, and "totalling $0.00"
  // reads like a reason to allow the removal rather than a reason to refuse
  // it, so the money clause is dropped when there is no money.
  const money = total === 0 ? "" : ` totalling ${formatAmount(total)}`;

  return {
    allowed: false,
    refusal: "has_values",
    title: "Clear these months first",
    message:
      `"${input.categoryName}" still has ${describeValueCount(input.occupied.length)}${money} on ${where}. ` +
      `Removing the category would leave those numbers in the database with no row on any P&L to show them. ` +
      `Open each project's P&L tab, clear the category's cell for those months, then remove the category here. ` +
      `Removing it here removes it from every project, so if it is only some projects that do not use this line, ` +
      `hide it on those projects' P&L tabs instead, or clear it there, and leave the category in place for the rest.`,
  };
}
